/**
 * Web Push клиент (W3, NIKA-PWA-SPEC-v1 §11): SW-регистрация, подписка/отписка,
 * iOS-гайд «Добавить на экран Домой» (§11: iOS push — только standalone;
 * не-standalone → фолбэк TG, I-7).
 *
 * Тело пуша клиент не формирует — санитайз на сервере (runtime/webpush.py):
 * на Lock Screen никогда нет веса/мед-данных.
 */
import {
  getPushStatus,
  subscribePush,
  unsubscribePush,
} from "./api";

export type PushEnableResult =
  | "subscribed"
  | "unsupported"
  | "disabled" // сервер без VAPID (push-контур закрыт)
  | "needs_ios_install"
  | "denied"
  | "error";

/** Регистрирует /sw.js (best-effort; вызывается из bootstrap). */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register("/sw.js").catch(() => {
    /* push — опциональная оптимизация, фолбэк TG (I-7) */
  });
}

/** iOS вне standalone (Safari-вкладка): push недоступен → нужен гайд. */
export function isIosStandaloneNeeded(): boolean {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    (navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;
  return ios && !standalone;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function isPushSubscribed(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    return Boolean(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** Включает push: permission → подписка → POST /push/subscribe (D4). */
export async function enablePush(): Promise<PushEnableResult> {
  try {
    if (isIosStandaloneNeeded()) return "needs_ios_install";
    if (
      !("serviceWorker" in navigator) ||
      !("PushManager" in window) ||
      !("Notification" in window)
    ) {
      return "unsupported";
    }
    const status = await getPushStatus();
    if (!status.configured || !status.vapid_public_key) return "disabled";
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          status.vapid_public_key,
        ) as unknown as BufferSource,
      }));
    const key = sub.getKey("p256dh");
    const authKey = sub.getKey("auth");
    if (!key || !authKey) return "error";
    const b64 = (buf: ArrayBuffer): string => {
      const bytes = new Uint8Array(buf);
      let s = "";
      for (const b of bytes) s += String.fromCharCode(b);
      return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    };
    await subscribePush({
      endpoint: sub.endpoint,
      keys: { p256dh: b64(key), auth: b64(authKey) },
    });
    return "subscribed";
  } catch {
    return "error";
  }
}

/** Отключает: DELETE /push/subscribe + отписка в браузере (disabled_at, D4). */
export async function disablePush(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      await unsubscribePush(sub.endpoint).catch(() => {
        /* best-effort: серверная строка умрёт по retention */
      });
      await sub.unsubscribe();
    }
    return true;
  } catch {
    return false;
  }
}
